/**
 * Crow — perches on a high platform, won't move until fed (Babylon.js port).
 *
 * When food is delivered to its nest, the crow drops a key item
 * (gear piece) and flies away (upward animation + fade).
 *
 * Visual: optional authored perch/flight motion with a static source fallback.
 */
import { Scene } from "@babylonjs/core/scene";
import { Matrix, Quaternion, Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { Animation } from "@babylonjs/core/Animations/animation";
import { NPC, NPCConfig } from "./NPC";
import { gameEvents } from "../../core/EventBus";
import { GameEvents } from "../../config/events";

import type { AbstractMesh } from "@babylonjs/core/Meshes/abstractMesh";
import type { ShadowGenerator } from "@babylonjs/core/Lights/Shadows/shadowGenerator";
import { CrowAnimator } from "./CrowAnimator";

export interface CrowVisualOptions {
  animated?: boolean;
  shadowGenerator?: ShadowGenerator;
}

export class Crow extends NPC {
  override get species(): string { return "crow"; }

  private hasBeenFed = false;
  private droppedItem = false;
  private baseY: number;
  private departureElapsed = 0;
  private animator: CrowAnimator | null = null;
  private casters: AbstractMesh[] = [];

  constructor(scene: Scene, key: string, position: Vector3, private readonly visuals: CrowVisualOptions = {}) {
    const config: NPCConfig = {
      key,
      position,
      height: 0.5,
      radius: 0.2,
      color: '#222222',
      behavior: {
        initialState: 'idle',
      },
      modelPath: visuals.animated ? "/assets/models/npcs/crow_anim.glb" : "/assets/models/npcs/crow.glb",
      modelScale: 3.0,
    };

    super(scene, config);
    this.baseY = position.y;
    if (visuals.animated) {
      // The unchanged root is the perch origin. Source talons start .015u
      // above it, matching the existing rail top; the fallback shares it.
      this.mesh.bakeTransformIntoVertices(Matrix.Translation(0, .265, 0));
      this.mesh.rotation.y = Math.PI;
    }
    if (visuals.shadowGenerator) this.registerCaster(this.mesh);

    // Dark feathered appearance
    this.material.diffuseColor = new Color3(0.12, 0.12, 0.14);
    this.material.emissiveColor = new Color3(0.03, 0.03, 0.04);
  }

  protected override onVisualModelLoaded(): void {
    if (!this.visualModel) return;
    if (this.visuals.animated) {
      // Restore the glTF conversion basis, with parent yaw facing the
      // approaching cats and bringing the right-eye monocle to the camera.
      for (const root of this.visualModel.rootNodes) {
        root.scaling.set(3, 3, -3);
        root.rotationQuaternion = Quaternion.Identity();
        root.rotation.setAll(0);
      }
    }
    this.clearCasters();
    if (!this.mesh.isEnabled()) {
      for (const group of this.visualModel.animationGroups) group.stop();
      return;
    }
    if (this.visuals.shadowGenerator) {
      for (const mesh of this.visualModel.meshes) if (mesh.getTotalVertices() > 0) this.registerCaster(mesh);
    }
    if (this.visuals.animated) this.animator = CrowAnimator.create(this.visualModel.animationGroups,
      this.hasBeenFed ? this.departureElapsed : null);
  }

  private registerCaster(mesh: AbstractMesh): void {
    mesh.receiveShadows = true;
    this.visuals.shadowGenerator?.addShadowCaster(mesh, false);
    this.casters.push(mesh);
  }

  private clearCasters(): void {
    for (const mesh of this.casters) this.visuals.shadowGenerator?.removeShadowCaster(mesh, false);
    this.casters = [];
  }

  private stopSourceMotion(): void {
    this.animator?.dispose(); this.animator = null;
    this.clearCasters();
  }

  /** Feed the crow. Returns true if this is the first feeding. */
  feed(): boolean {
    if (this.hasBeenFed) return false;
    this.hasBeenFed = true;
    this.departureElapsed = 0;
    this.animator?.depart();

    // Happy reaction — brief hop animation then drop item
    const hopAnim = new Animation(
      `crow_${this.npcKey}_hop`,
      "position.y",
      30,
      Animation.ANIMATIONTYPE_FLOAT,
      Animation.ANIMATIONLOOPMODE_CONSTANT,
    );
    hopAnim.setKeys([
      { frame: 0, value: this.mesh.position.y },
      { frame: 5, value: this.mesh.position.y + 1.0 },
      { frame: 10, value: this.mesh.position.y },
    ]);

    this.mesh.animations = [hopAnim];
    this.scene.beginAnimation(this.mesh, 0, 10, false, 1.0, () => {
      this.dropItem();
    });

    return true;
  }

  private dropItem(): void {
    if (this.droppedItem) return;
    this.droppedItem = true;

    // Emit event for scene to create the dropped gear
    gameEvents.emit(GameEvents.CROW_DROPPED_ITEM, {
      npcKey: this.npcKey,
      x: this.mesh.position.x,
      y: this.mesh.position.y - 0.5,
    });

    // Fly away — move upward and fade out
    const flyAnim = new Animation(
      `crow_${this.npcKey}_fly`,
      "position.y",
      30,
      Animation.ANIMATIONTYPE_FLOAT,
      Animation.ANIMATIONLOOPMODE_CONSTANT,
    );
    flyAnim.setKeys([
      { frame: 0, value: this.mesh.position.y },
      { frame: 45, value: this.mesh.position.y + 15 },
    ]);

    const fadeAnim = new Animation(
      `crow_${this.npcKey}_fade`,
      "visibility",
      30,
      Animation.ANIMATIONTYPE_FLOAT,
      Animation.ANIMATIONLOOPMODE_CONSTANT,
    );
    fadeAnim.setKeys([
      { frame: 0, value: 1 },
      { frame: 45, value: 0 },
    ]);

    this.mesh.animations = [flyAnim, fadeAnim];
    this.scene.beginAnimation(this.mesh, 0, 45, false, 1.0, () => {
      this.syncVisualVisibility(0);
      this.mesh.isVisible = false;
      this.mesh.setEnabled(false);
      this.stopSourceMotion();
    });
  }

  get isFed(): boolean {
    return this.hasBeenFed;
  }

  override update(time: number, deltaSeconds: number): void {
    if (!this.mesh.isEnabled()) { this.stopSourceMotion(); return; }
    if (this.hasBeenFed) this.departureElapsed += Math.max(0, deltaSeconds);
    this.animator?.update(deltaSeconds);
    // Mesh visibility is not inherited by the imported feather/foot meshes.
    // Keep the authored shell in the existing departure fade's own state.
    if (this.hasBeenFed) this.syncVisualVisibility(this.mesh.visibility);
    // Gentle bob while idle (not yet fed)
    if (!this.hasBeenFed && !this.visuals.animated) {
      this.mesh.position.y = this.baseY + Math.sin(time * 2) * 0.03;
    }
  }

  override dispose(): void {
    this.stopSourceMotion();
    super.dispose();
  }

}
